import { Link } from "react-router-dom";
import { Logo } from "@/components/ui/Logo";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Heart, Mail, Shield, Lock, Users, Award } from "lucide-react";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-teal-50 to-emerald-50/80 dark:from-slate-900 dark:via-purple-950/95 dark:to-slate-950 border-t border-border mt-auto text-foreground/80 dark:text-slate-300">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Logo size="md" />
            </div>
            <p className="text-sm text-foreground/60 dark:text-slate-400">
              A playful chess playground created by Chess Lovers for young chess enthusiasts worldwide.
            </p>
            <div className="flex items-center gap-1 text-xs text-teen-pink">
              <span>Made with</span>
              <Heart className="h-3 w-3 fill-current" />
              <span>in Sydney, Australia</span>
            </div>
            
            {/* Trust Signals */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge className="text-xs bg-teen-green/20 text-teen-green border-teen-green/30">
                <Shield className="w-3 h-3 mr-1" />
                HTTPS Secured
              </Badge>
              <Badge className="text-xs bg-teen-cyan/20 text-teen-cyan border-teen-cyan/30">
                <Users className="w-3 h-3 mr-1" />
                Safe for Kids
              </Badge>
            </div>
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm bg-gradient-to-r from-teen-pink to-teen-purple bg-clip-text text-transparent">Legal</h3>
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/user-agreement" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-cyan transition-colors"
              >
                User Agreement
              </Link>
              <Link 
                to="/privacy-policy" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-cyan transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/fair-play-policy" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-cyan transition-colors"
              >
                Fair Play Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-cyan transition-colors"
              >
                Terms of Service
              </Link>
            </nav>
          </div>

          {/* Platform Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm bg-gradient-to-r from-teen-cyan to-teen-green bg-clip-text text-transparent">Platform</h3>
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/hatch-rules" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-pink transition-colors"
              >
                Hatch Evolution Rules
              </Link>  
              <Link 
                to="/about" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-green transition-colors"
              >
                About ChessHatch
              </Link>
              <Link 
                to="/how-it-works" 
                className="text-sm text-foreground/60 dark:text-slate-400 hover:text-teen-cyan transition-colors"
              >
                How It Works
              </Link>
              {/* Hidden: Community link - preserved for future activation */}
              {false && (
                <Link 
                  to="/community" 
                  className="text-sm text-slate-400 hover:text-teen-green transition-colors"
                >
                  Community
                </Link>
              )}
            </nav>
          </div>

          {/* Contact & Social */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm bg-gradient-to-r from-teen-gold to-teen-orange bg-clip-text text-transparent">Connect</h3>
            <div className="space-y-2">
              <p className="text-sm text-foreground/60 dark:text-slate-400">
                Questions or feedback?
              </p>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-teen-gold" />
                <span className="text-sm text-foreground/60 dark:text-slate-400">
                  support@chesshatch.co
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Challenger Disclaimer */}
        <div className="mt-6 p-4 rounded-lg bg-teen-cyan/10 dark:bg-teen-cyan/5 border border-teen-cyan/20">
          <p className="text-xs text-foreground/60 dark:text-slate-400 text-center">
            <span className="font-semibold">🐣 ChessHatch is still hatching!</span> To avoid empty queues 
            and keep things fun, we run multiple AI Challenger accounts. They may use fictional 
            names/avatars and can appear in matches and on the leaderboard. They're here to keep 
            the arena active — humans are still the main event!
          </p>
        </div>

        <Separator className="my-6 bg-primary/20" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-xs text-foreground/50 dark:text-slate-500 text-center md:text-left">
            <p>© {currentYear} ChessHatch. Created by Chess Lovers.</p>
            <p className="mt-1">Empowering young minds through chess since 2024. ♟️</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge className="text-xs bg-teen-blue/20 text-teen-blue border-teen-blue/30">
              <Award className="w-3 h-3 mr-1" />
              Educational Platform
            </Badge>
            <Badge className="text-xs bg-teen-purple/20 text-teen-purple border-teen-purple/30">
              <Lock className="w-3 h-3 mr-1" />
              We Never Sell Your Data
            </Badge>
            <span className="text-foreground/50 dark:text-slate-500">Cloudflare Protected</span>
          </div>
        </div>
      </div>
    </footer>
  );
}